import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import accountService from '../../../shared/services/account-service';
import Header from '../../../shared/components/header/page';
import Content from '../../../shared/components/content/page';
import useLoaderDisplay from '../../../shared/hooks/loader-display';
import { useCircleQueries } from '../../../shared/tanstack/circle/queries';
import { useAccountMutations } from '../../../shared/tanstack/account/mutations';
import { useMutation } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { FirebaseAnalytics } from '@capacitor-firebase/analytics';
import InputVerificationCode from '../../../shared/components/input/verification-code/page';
import { formattedPhoneNumber } from '../../../shared/services/format-service';
import { useAccountQueries } from '../../../shared/tanstack/account/queries';

interface State {
	phoneNumber?: string;
	countryCode?: number;
}

export default function VerifyOtp() {
	const navigate = useNavigate();
	const { phoneNumber, countryCode } = useLocation().state as State; // Read values passed on state
	if (!phoneNumber || !countryCode) {
		throw new Error('Need Phone & CountryCode');
	}

	const [skipCirclesQuery, setSkipCirclesQuery] = useState(true);
	const [skipAccountsQuery, setSkipAccountsQuery] = useState(true);

	const { accountQuery } = useAccountQueries();
	const { data: account } = useQuery(accountQuery(skipAccountsQuery));

	const { verifyOtpMutation } = useAccountMutations();
	const {
		mutate: verifyOtp,
		isSuccess: isCodeVerified,
		isPaused: isSendingOtpPaused,
		status,
	} = useMutation(verifyOtpMutation);

	const { circlesQuery } = useCircleQueries();
	const { data: circles, isLoading: isCirclesLoading } = useQuery(circlesQuery(skipCirclesQuery));

	useLoaderDisplay({
		key: 'verifyOtp',
		isLoading: status === 'pending' && !isSendingOtpPaused,
	});
	useLoaderDisplay({ key: 'getCircles', isLoading: isCirclesLoading });

	const verifyWithCode = (verificationCode: string) => {
		const fcmToken = accountService.getFCMToken() ?? undefined;
		const referrerId = localStorage.getItem('referrerId') ?? undefined;
		verifyOtp({
			phoneNumber,
			countryCode,
			otp: verificationCode,
			fcmToken,
			referrerId,
		});
	};

	useEffect(() => {
		if (isCodeVerified) {
			setSkipAccountsQuery(false);
		}
	}, [isCodeVerified]);

	useEffect(() => {
		if (account && phoneNumber && countryCode) {
			accountService.setAccount(account);
			const loggedInUserId = account.id;
			FirebaseAnalytics.setUserId({
				userId: loggedInUserId,
			}).catch(() => {
				// Do nothing
			});

			// If the name for the account is already set, request list of circles
			if (account.name) {
				setSkipCirclesQuery(false);
			}
			// Else open the update profile page
			else {
				accountService.resetProfileCreated();
				navigate('/secure/settings/profile', {
					state: {
						phoneNumber,
						countryCode,
					},
					replace: true,
				});
			}
		}
	}, [account, navigate, phoneNumber, countryCode]);

	useEffect(() => {
		if (account && circles && !skipCirclesQuery) {
			// if circles are created go to home screen else, go to circle creation screen
			navigate(circles.length > 0 ? '/secure/circles' : '/secure/onboarding/circle-info', {
				replace: true,
			});
		}
	}, [circles, account, skipCirclesQuery, navigate]);

	return (
		<>
			{countryCode && phoneNumber && (
				<Header
					title="Verify Code"
					subtitle={`enter the code sent to ${formattedPhoneNumber(phoneNumber, countryCode)}`}
				/>
			)}
			<Content>
				<p className="text-2xl">Code</p>
				<div className="flex space-x-3">
					<InputVerificationCode
						onCodeEntered={(verificationCode: string) => {
							verifyWithCode(verificationCode);
						}}
					/>
				</div>
				<div>
					By verifying the OTP you agree to the{' '}
					<Link key="terms" to={`/terms`} style={{ textDecoration: 'underline' }}>
						terms & conditions
					</Link>{' '}
					&{' '}
					<Link key="privacy" style={{ textDecoration: 'underline' }} to={`/privacy`}>
						privacy policy
					</Link>{' '}
				</div>
			</Content>
		</>
	);
}
